import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { encolarAccion, esFaltaDeSenal, yaHayPendiente } from '@/lib/campo/colaDeAcciones'
import { esSuDia, medirContraLaGeocerca, sitioGuardadoDe } from '@/lib/campo/geocercaLocal'
import { leerTrabajoGuardado } from '@/modules/campo/useMiTrabajo'
import { mensajeDeError } from '@/lib/supabase/errores'
import { obtenerPosicionActual } from '@/lib/nativo/ubicacion'
import { subirEvidencia } from '@/modules/campo/useEvidencia'
import { mapConLimite } from '@/shared/utilidades/concurrencia'
import type { ItemSobrante, Visita } from '@/lib/supabase/tipos-bd'

/**
 * Llegada y salida.
 *
 * El reloj de la ejecución corre desde que el operario llega hasta que sale,
 * no desde que alguien cambia un estado en una pantalla.
 */

export type PuntoDelTelefono = {
  latitud: number
  longitud: number
  /** Lo que el teléfono dice que se equivoca, en metros. */
  exactitud: number
}

/**
 * Pide la ubicación al teléfono.
 *
 * Se pide con precisión alta y se espera hasta veinte segundos: el GPS frío
 * tarda, y rendirse a los cinco segundos devuelve el punto de la antena, que
 * puede estar a un kilómetro.
 *
 * maximumAge en cero a propósito: un punto guardado de hace media hora es el
 * de la casa anterior, y aquí lo que se está probando es que ESTÁ AQUÍ.
 *
 * Devuelve null si la persona no da permiso o el teléfono no puede. Sin punto
 * no se puede marcar la llegada, porque la salida no siempre se marca y este
 * punto es la única prueba que queda de la visita.
 */
export async function pedirUbicacion(): Promise<PuntoDelTelefono | null> {
  // Dentro del APK va por el plugin nativo; en la web, por el navegador. Antes
  // usaba navigator.geolocation directo, que en el WebView de Android reporta
  // «permiso negado» aunque el permiso del sistema esté concedido.
  const pos = await obtenerPosicionActual({ timeout: 20_000, maximumAge: 0 })
  if (!pos) return null
  return {
    latitud: pos.coords.latitude,
    longitud: pos.coords.longitude,
    exactitud: pos.coords.accuracy,
  }
}

/**
 * Lo pide dos veces antes de rendirse.
 *
 * El primer intento con el GPS frío falla seguido, y el segundo —ya con la
 * radio encendida— suele salir. Rendirse al primero manda a la gente al
 * camino de la excepción sin necesidad.
 */
export async function pedirUbicacionConReintento(): Promise<PuntoDelTelefono | null> {
  const primero = await pedirUbicacion()
  if (primero) return primero

  await new Promise((seguir) => setTimeout(seguir, 1500))
  return pedirUbicacion()
}

export function useVisitas(ordenTrabajoId: string | null) {
  const consulta = useQuery({
    queryKey: ['visitas', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Visita[]> => {
      const { data, error } = await supabase
        .from('vista_visitas')
        .select('*')
        .eq('orden_trabajo_id', ordenTrabajoId!)
        .order('momento', { ascending: true })

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as Visita[]
    },
  })

  const visitas = consulta.data ?? []
  const ultima = visitas[visitas.length - 1]

  return {
    visitas,
    /** Está adentro: la última marca fue una llegada. */
    enSitio: ultima?.tipo === 'llegada',
    cargando: consulta.isLoading,
  }
}

function useRefrescar(ordenTrabajoId: string | null) {
  const clienteConsultas = useQueryClient()

  /*
   * REFRESCAR NO ES PARTE DE LA OPERACIÓN.
   *
   * Esto se esperaba con `await`, y como `mutateAsync` no resuelve hasta que
   * termina su `onSuccess`, la marca quedaba atada a que ONCE consultas
   * volvieran del servidor. Sin señal eso no pasa nunca: el botón «Llegué» se
   * quedaba en «Tomando ubicación…» para siempre aunque la llegada YA
   * estuviera guardada en el teléfono —medido, con la traza llegando hasta el
   * final de la función—. Y con mala cobertura pasaba lo mismo, solo que
   * tardando.
   *
   * Invalidar es avisarle a la pantalla de que hay datos nuevos. Se lanza y
   * se sigue: si una consulta no puede refrescarse ahora, se refrescará
   * cuando haya red, y mientras tanto nadie se queda mirando un botón muerto.
   */
  return async () => {
    void Promise.all(
      [
        // La pantalla del técnico sale de fn_mi_trabajo (clave 'mi-trabajo').
        // Sin invalidarla, tras «Terminar» la tarjeta tardaba hasta un minuto
        // en volver el botón a «Llegué», porque solo se refrescaba al vencer
        // el intervalo. Es lo primero que tiene que refrescar.
        ['mi-trabajo'],
        // Al terminar, el trabajo aparece en el historial.
        ['mi-historial'],
        ['visitas', ordenTrabajoId],
        ['ordenes-servicio'],
        ['cronograma'],
        ['recorridos'],
        ['trazabilidad'],
        ['lo-que-espera'],
        ['ordenes-ubicadas'],
        ['ordenes-sin-ubicar'],
      ].map((clave) => clienteConsultas.invalidateQueries({ queryKey: clave })),
    ).catch(() => {
      /* Que no se pueda refrescar ahora no invalida lo que ya se hizo. */
    })
  }
}

/** Cuando el teléfono no da la ubicación y no hay más remedio. */
export class SinUbicacion extends Error {
  constructor() {
    super('El teléfono no dio la ubicación.')
    this.name = 'SinUbicacion'
  }
}

/**
 * El operario intentó iniciar estando fuera de la geocerca del sitio.
 *
 * No es un error del sistema: es una ADVERTENCIA —está bloqueado hasta llegar
 * al sitio— y por eso la pantalla la muestra distinta a un error. La base la
 * marca con el código GEO01 (fn_marcar_llegada); el mensaje ya viene listo y
 * dice a cuántos metros está y cuánto mide la cerca.
 */
export class FueraDeLaGeocerca extends Error {
  constructor(mensaje: string) {
    super(mensaje)
    this.name = 'FueraDeLaGeocerca'
  }
}

export function useMarcarLlegada(ordenTrabajoId: string | null) {
  const refrescar = useRefrescar(ordenTrabajoId)

  return useMutation({
    mutationFn: async (): Promise<{ distancia: number | null; aviso: string | null }> => {
      const punto = await pedirUbicacionConReintento()

      // Sin punto no se marca. No hay excepción que el interesado se pueda
      // firmar solo: se pide autorización, y eso lo maneja la pantalla.
      if (!punto) throw new SinUbicacion()

      /*
       * SIN SEÑAL SE MARCA IGUAL.
       *
       * En muchas obras no hay cobertura y el botón fallaba: el técnico
       * entraba a trabajar sin que constara la hora. Ahora la llegada se
       * guarda en el teléfono CON LA HORA DEL GESTO y sube cuando vuelve la
       * red. La hora es lo que había que salvar: enviarla dos horas después
       * sin más haría que el sistema anotara dos horas de retraso que nadie
       * tuvo.
       */
      const sinSenal = async () => {
        /*
         * LA GEOCERCA SE MIDE AQUÍ TAMBIÉN, Y SE MIDE ANTES DE ENCOLAR.
         *
         * La regla sigue viviendo en la base —ella la vuelve a medir al subir
         * y su palabra es la que vale—, pero sin señal no contesta, y el
         * técnico se enteraba del rechazo horas después, cuando ya no podía
         * caminar los metros que le faltaban. El punto y el radio están
         * guardados desde la última vez que hubo cobertura; el GPS no
         * necesita señal para dar la posición. Con eso alcanza para decírselo
         * en la cara, con las mismas palabras que usaría la base.
         */
        const sitio = sitioGuardadoDe(leerTrabajoGuardado(), ordenTrabajoId!)

        // EL DÍA, ANTES QUE EL SITIO. Estar parado en la obra correcta el día
        // equivocado no sirve de nada, y decirle «estás a 1.300 m» cuando el
        // problema es que el trabajo es de mañana lo manda a buscar donde no
        // es. La base lo rechaza igual al subir (FEC01, migración 0216).
        const dia = esSuDia(sitio)
        if (!dia.suDia) throw new Error(dia.mensaje)

        const fuera = medirContraLaGeocerca(sitio, punto)
        if (!fuera.dentro) throw new FueraDeLaGeocerca(fuera.mensaje)

        const repetida = await yaHayPendiente('llegada', ordenTrabajoId!)

        await encolarAccion('llegada', ordenTrabajoId!, {
          p_latitud: punto.latitud,
          p_longitud: punto.longitud,
          p_exactitud_m: punto.exactitud,
        })

        return {
          distancia: null,
          aviso: repetida
            ? `Esta llegada ya estaba guardada (${new Date(repetida.ocurridoEn).toLocaleTimeString(
                'es-CO',
                { hour: '2-digit', minute: '2-digit' },
              )}). Se mantiene esa hora y se enviará cuando vuelva la señal.`
            : 'Sin señal: la llegada quedó guardada en el teléfono con la hora de ahora y se enviará sola cuando vuelva la conexión.',
        }
      }

      if (!navigator.onLine) return sinSenal()

      const { data, error } = await supabase.rpc('fn_marcar_llegada', {
        p_orden_trabajo_id: ordenTrabajoId!,
        p_latitud: punto.latitud,
        p_longitud: punto.longitud,
        p_exactitud_m: punto.exactitud,
      })

      // SEÑAL DÉBIL CUENTA COMO SIN SEÑAL. El teléfono dice tener red, la
      // petición no llega, y antes el técnico veía un error y la marca no se
      // guardaba en ningún lado. Ahora se guarda igual que sin red.
      if (error && esFaltaDeSenal(error)) return sinSenal()

      // Estar fuera de la geocerca no es un error: es una advertencia. La base
      // la distingue con el código GEO01 para que la pantalla la trate aparte.
      if (error?.code === 'GEO01') throw new FueraDeLaGeocerca(mensajeDeError(error))
      if (error) throw new Error(mensajeDeError(error))

      const fila = (
        data as unknown as Array<{ distancia_m: number | null; aviso: string | null }>
      )?.[0]

      return {
        distancia:
          fila?.distancia_m === null || fila?.distancia_m === undefined
            ? null
            : Number(fila.distancia_m),
        aviso: fila?.aviso ?? null,
      }
    },
    onSuccess: refrescar,
  })
}

/**
 * Pedir permiso para empezar sin ubicación.
 *
 * No es una excepción que el operario se conceda: es una solicitud que sale
 * en el tablero del Gerente operativo, como todo lo demás que espera decisión.
 */
export function usePedirInicioSinUbicacion(ordenTrabajoId: string | null) {
  const refrescar = useRefrescar(ordenTrabajoId)

  return useMutation({
    mutationFn: async (motivo: string): Promise<void> => {
      /*
       * SIN SEÑAL TAMBIÉN SE PIDE, y esto no es un extra: es lo que sostiene
       * el mensaje de la geocerca.
       *
       * Cuando el GPS falla y la medida local lo frena, lo que se le dice es
       * «si de verdad estás aquí, pide autorización». Si ese botón necesita
       * cobertura, el técnico se queda sin poder marcar Y sin poder pedir
       * permiso, justo en el único sitio donde eso pasa: una obra sin señal.
       * La petición se guarda con la hora en que la hizo, porque el gerente
       * operativo decide mirando cuándo la pidió.
       */
      if (!navigator.onLine) {
        await encolarAccion('autorizacion', ordenTrabajoId!, { p_motivo: motivo })
        return
      }

      const { error } = await supabase.rpc('fn_pedir_inicio_sin_ubicacion', {
        p_orden_trabajo_id: ordenTrabajoId!,
        p_motivo: motivo,
      })

      if (error && esFaltaDeSenal(error)) {
        await encolarAccion('autorizacion', ordenTrabajoId!, { p_motivo: motivo })
        return
      }
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useMarcarSalida(ordenTrabajoId: string | null) {
  const refrescar = useRefrescar(ordenTrabajoId)

  return useMutation({
    mutationFn: async ({
      comentario,
      archivos,
      servicio,
      sobrantes,
      fotosSobrante = [],
    }: {
      comentario: string
      archivos: File[]
      /** El servicio, como contexto para que la IA redacte mejor. Opcional. */
      servicio?: string | null
      /** Qué sobró de los materiales; [] = nada (0310). Viaja con la salida. */
      sobrantes: ItemSobrante[]
      /** Foto del material que sobró: obligatoria si sobró algo (0312). */
      fotosSobrante?: File[]
    }): Promise<number> => {
      // Terminar también exige estar en el sitio (geocerca), igual que llegar:
      // se pide la ubicación con reintento porque el GPS frío falla al primero.
      const punto = await pedirUbicacionConReintento()

      /*
       * SIN SEÑAL TAMBIÉN SE TERMINA.
       *
       * Es el caso más caro de los tres: la salida lleva cinco fotos, y sin
       * cobertura no se pueden subir al almacén —y la base pide rutas, no
       * imágenes—. Así que las fotos se guardan enteras en el teléfono junto
       * con el marcaje; al volver la señal se suben primero y solo entonces
       * se registra la salida, para que nunca quede un cierre sin evidencia.
       *
       * El acta redactada por la IA no se intenta aquí: necesita internet.
       * La base cae al texto del técnico, que es el que importa.
       */
      const sinSenal = async (): Promise<number> => {
        // Terminar exige estar en el sitio igual que llegar, así que se avisa
        // igual. Sin punto no se juzga: la base tampoco lo hace.
        if (punto) {
          const fuera = medirContraLaGeocerca(
            sitioGuardadoDe(leerTrabajoGuardado(), ordenTrabajoId!),
            punto,
            'terminar',
          )
          if (!fuera.dentro) throw new FueraDeLaGeocerca(fuera.mensaje)
        }

        const repetida = await yaHayPendiente('salida', ordenTrabajoId!)

        await encolarAccion(
          'salida',
          ordenTrabajoId!,
          {
            p_latitud: punto?.latitud ?? null,
            p_longitud: punto?.longitud ?? null,
            p_exactitud_m: punto?.exactitud ?? null,
            p_comentario: comentario,
            // La IA no se puede llamar sin señal. Se deja en nulo y la cola
            // pide la redacción al subir, que es cuando ya hay internet.
            p_resumen_acta: null,
            // Lo que sobró viaja en la misma salida (0310).
            p_sobrantes: sobrantes,
          },
          archivos,
          servicio,
          fotosSobrante,
        )

        if (repetida) {
          throw new Error(
            'Esta salida ya estaba guardada en el teléfono y se enviará cuando vuelva la señal.',
          )
        }

        // -1: «quedó en el teléfono». No hay con qué calcular las horas aquí,
        // y devolver 0 hacía que la pantalla dijera «quedaron registradas 0
        // horas», que es falso por los dos lados.
        return -1
      }

      if (!navigator.onLine) return sinSenal()

      // Las fotos de evidencia se suben PRIMERO al bucket privado; a la base
      // solo le viajan las rutas. Si la subida falla, no se marca la salida.
      // Van de a varias a la vez (tope de tres): en serie, cinco fotos sobre
      // datos móviles hacían de este el paso más lento de todo el cierre.
      // Si fallan por la señal, el cierre se guarda entero en el teléfono.
      let rutas: string[]
      let rutasSobrante: string[]
      try {
        rutas = await mapConLimite(archivos, 3, (archivo) =>
          subirEvidencia(ordenTrabajoId!, archivo),
        )
        rutasSobrante = await mapConLimite(fotosSobrante, 3, (archivo) =>
          subirEvidencia(ordenTrabajoId!, archivo),
        )
      } catch (fallo) {
        if (esFaltaDeSenal(fallo as Error)) return sinSenal()
        throw fallo
      }

      // La IA mejora la redacción de lo que escribió el técnico para el acta del
      // cliente. Es automática y sin revisión; si falla, se sigue con el texto
      // crudo (la base cae a resumen_trabajo, así el acta nunca queda vacía).
      let resumenActa: string | null = null
      try {
        const { data: mejora } = await supabase.functions.invoke('mejorar-acta', {
          body: { texto: comentario, servicio: servicio ?? '' },
        })
        const redaccion = (mejora as { redaccion?: string | null } | null)?.redaccion
        resumenActa = redaccion?.trim() || null
      } catch {
        // Sin IA no se bloquea el cierre: el técnico ya está en el sitio.
        resumenActa = null
      }

      const { data, error } = await supabase.rpc('fn_marcar_salida', {
        p_orden_trabajo_id: ordenTrabajoId!,
        p_latitud: punto?.latitud ?? null,
        p_longitud: punto?.longitud ?? null,
        p_exactitud_m: punto?.exactitud ?? null,
        p_comentario: comentario,
        p_fotos: rutas,
        p_resumen_acta: resumenActa,
        p_sobrantes: sobrantes,
        p_sobrantes_fotos: rutasSobrante,
      })

      if (error && esFaltaDeSenal(error)) return sinSenal()

      // Estar fuera del sitio no es un error rojo: es una advertencia (GEO01),
      // igual que en la llegada. El botón sigue disponible para reintentar
      // cuando de verdad esté en el sitio.
      if (error?.code === 'GEO01') throw new FueraDeLaGeocerca(mensajeDeError(error))
      if (error) throw new Error(mensajeDeError(error))

      return Number(data)
    },
    onSuccess: refrescar,
  })
}

/**
 * Reportar un avance del día.
 *
 * Es «Terminar» a medias: deja evidencia (descripción + fotos) y para el reloj
 * del día, pero NO cierra la orden. La obra se queda en ejecución para retomarla
 * al día siguiente —al quedar la última marca como «avance», la tarjeta vuelve a
 * mostrar «Llegué»—. Exige estar en el sitio, igual que terminar; la base pide
 * mínimo 3 fotos.
 */
export function useReportarAvance(ordenTrabajoId: string | null) {
  const refrescar = useRefrescar(ordenTrabajoId)

  return useMutation({
    mutationFn: async ({
      comentario,
      archivos,
    }: {
      comentario: string
      archivos: File[]
    }): Promise<number> => {
      // Igual que al terminar: hay que estar en el sitio, y el GPS frío falla al
      // primero, así que se pide con reintento.
      const punto = await pedirUbicacionConReintento()

      /*
       * SIN SEÑAL SE GUARDA ANTES DE INTENTAR SUBIR NADA.
       *
       * Estaba al revés: primero subía las fotos al almacén y después
       * miraba si había red. Sin cobertura la subida reventaba y la función
       * se iba por el error, así que el avance NUNCA llegaba a guardarse: el
       * técnico escribía lo que hizo, adjuntaba tres fotos y lo perdía todo.
       * Lo destapó el barrido de botones; en el cierre ya estaba bien puesto.
       */
      const sinSenal = async (): Promise<number> => {
        // El avance pide geocerca como los otros dos.
        if (punto) {
          const fuera = medirContraLaGeocerca(
            sitioGuardadoDe(leerTrabajoGuardado(), ordenTrabajoId!),
            punto,
            'reportar el avance',
          )
          if (!fuera.dentro) throw new FueraDeLaGeocerca(fuera.mensaje)
        }

        await encolarAccion(
          'avance',
          ordenTrabajoId!,
          {
            p_latitud: punto?.latitud ?? null,
            p_longitud: punto?.longitud ?? null,
            p_exactitud_m: punto?.exactitud ?? null,
            p_comentario: comentario,
          },
          archivos,
        )
        return -1
      }

      if (!navigator.onLine) return sinSenal()

      // Con señal: las fotos primero al bucket; a la base solo le viajan las
      // rutas. De a varias a la vez (tope de tres), como en el cierre.
      let rutas: string[]
      try {
        rutas = await mapConLimite(archivos, 3, (archivo) =>
          subirEvidencia(ordenTrabajoId!, archivo),
        )
      } catch (fallo) {
        if (esFaltaDeSenal(fallo as Error)) return sinSenal()
        throw fallo
      }

      const { data, error } = await supabase.rpc('fn_reportar_avance', {
        p_orden_trabajo_id: ordenTrabajoId!,
        p_latitud: punto?.latitud ?? null,
        p_longitud: punto?.longitud ?? null,
        p_exactitud_m: punto?.exactitud ?? null,
        p_comentario: comentario,
        p_fotos: rutas,
      })

      if (error && esFaltaDeSenal(error)) return sinSenal()

      // Estar fuera del sitio es una ADVERTENCIA (GEO01), igual que en la salida.
      if (error?.code === 'GEO01') throw new FueraDeLaGeocerca(mensajeDeError(error))
      if (error) throw new Error(mensajeDeError(error))

      return Number(data)
    },
    onSuccess: refrescar,
  })
}

/**
 * SALIR A ALMORZAR (0235).
 *
 * La orden se pausa —el tramo trabajado se cierra aquí— y ella y las que
 * siguen ese día se corren la hora de almuerzo. La vuelta es «Volver del
 * almuerzo», que es la llegada de siempre, con su geocerca.
 *
 * No exige estar en la obra al salir —se puede ir a almorzar a donde sea—,
 * pero si el teléfono da el punto, se guarda. Sin señal se guarda en el
 * teléfono con la hora del gesto, como todas las marcas.
 */
export function useSalirAAlmuerzo(ordenTrabajoId: string | null) {
  const refrescar = useRefrescar(ordenTrabajoId)

  return useMutation({
    mutationFn: async (): Promise<{ volver: string | null; sinSenal: boolean }> => {
      // Un intento corto: el punto es un dato de más, no una condición.
      const pos = await obtenerPosicionActual({ timeout: 5000, maximumAge: 60_000 })
      const punto = {
        p_latitud: pos?.coords.latitude ?? null,
        p_longitud: pos?.coords.longitude ?? null,
        p_exactitud_m: pos?.coords.accuracy ?? null,
      }

      const sinSenal = async () => {
        if (!(await yaHayPendiente('almuerzo', ordenTrabajoId!))) {
          await encolarAccion('almuerzo', ordenTrabajoId!, punto)
        }
        return { volver: null, sinSenal: true }
      }

      if (!navigator.onLine) return sinSenal()

      const { data, error } = await supabase.rpc('fn_salir_a_almuerzo', {
        p_orden_trabajo_id: ordenTrabajoId!,
        ...punto,
      })

      if (error && esFaltaDeSenal(error)) return sinSenal()

      if (error) throw new Error(mensajeDeError(error))
      return { volver: (data as unknown as string | null) ?? null, sinSenal: false }
    },
    onSuccess: refrescar,
  })
}
